/**
 * HeaderContext and Provider
 *
 * @fileoverview
 * Context and Provider to manage header design state.
 * Provides header state to all header-related components:
 * - Header component
 * - AboveHeader (Promo Banner)
 * - MegaMenu
 * - Logo component
 * - Search modal
 *
 * @see task-sticky-header.md for requirements
 */

import React, {createContext, useContext, useState, useMemo, useCallback, useEffect} from 'react';
import PropTypes from 'prop-types';

import {useBreakpointValue} from '@salesforce/retail-react-app/app/components/shared/ui';

import {useHeaderState} from '@amblique/king-living/app/hooks/use-header-state';
import {
  HEADER_DESIGN_STATE,
  HEADER_STYLE,
  STICKY_SCROLL_THRESHOLD,
  DEFAULT_HEADER_STATE,
} from '@amblique/king-living/app/constants/header';
import {
  getStickyHeaderHeightPx,
  syncPdpHideOnScrollProgrammaticScroll,
} from '@amblique/king-living/app/utils/utils';
import {isNearPageBottom} from '@amblique/king-living/app/utils/range-scroll-utils';

/**
 * Default context value
 */
const defaultContextValue = {
  headerState: DEFAULT_HEADER_STATE,
  headerStyle: HEADER_STYLE.LIGHT,
  originalState: DEFAULT_HEADER_STATE,
  isSticky: false,
  scrollY: 0,
  forcesSolid: false,
  stickyDisabled: false,
  isHeaderHidden: false,
  isMegaMenuOpen: false,
  isSearchOpen: false,
  isHeaderHovered: false,
  setMegaMenuOpen: () => {},
  setSearchOpen: () => {},
  setHeaderHovered: () => {},
  setHideOnScroll: () => {},
};

/**
 * HeaderContext
 */
const HeaderContext = createContext(defaultContextValue);

/**
 * HeaderProvider
 *
 * Provides header state to all header-related components.
 * Handles overrides when mega menu or search is open.
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @param {string} [props.configuredState='solid'] - Configured header state from Contentful
 * @returns {React.ReactElement}
 *
 * @example
 * <HeaderProvider configuredState="transparent">
 *   <Header />
 * </HeaderProvider>
 */
export function HeaderProvider({children, configuredState = DEFAULT_HEADER_STATE}) {
  const [isMegaMenuOpen, setIsMegaMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isHeaderHovered, setIsHeaderHovered] = useState(false);
  const [hideOnScroll, setHideOnScrollState] = useState(false);
  const isDesktop = useBreakpointValue({base: false, lg: true});

  const {
    headerState: baseState,
    headerStyle: baseStyle,
    originalState,
    isSticky,
    scrollY,
    scrollDirection,
    forcesSolid,
    stickyDisabled,
  } = useHeaderState(configuredState);

  // Wrapper functions for setting state
  const setMegaMenuOpen = useCallback((isOpen) => {
    setIsMegaMenuOpen(isOpen);
  }, []);

  const setSearchOpen = useCallback((isOpen) => {
    setIsSearchOpen(isOpen);
  }, []);

  const setHeaderHovered = useCallback((isHovered) => {
    setIsHeaderHovered(isHovered);
  }, []);

  // Override to solid when mega menu, search is open, or header is hovered
  // (hover only affects non-solid states)
  const headerState = useMemo(() => {
    if (isMegaMenuOpen || isSearchOpen) return HEADER_DESIGN_STATE.SOLID;

    // Hover transforms transparent/gradient to solid
    if (isHeaderHovered && baseState !== HEADER_DESIGN_STATE.SOLID) {
      return HEADER_DESIGN_STATE.SOLID;
    }

    return baseState;
  }, [isMegaMenuOpen, isSearchOpen, isHeaderHovered, baseState]);

  // Override style to light when mega menu, search is open, or header is hovered
  const headerStyle = useMemo(() => {
    if (isMegaMenuOpen || isSearchOpen) return HEADER_STYLE.LIGHT;

    // Hover transforms to light style (black icons/text)
    if (isHeaderHovered && baseStyle === HEADER_STYLE.DARK) {
      return HEADER_STYLE.LIGHT;
    }

    return baseStyle;
  }, [isMegaMenuOpen, isSearchOpen, isHeaderHovered, baseStyle]);

  // Hide-on-scroll only after scrolling past full sticky chrome (`--header-height`, set in _app).
  // Scroll up shows the menu unless mobile Range Detail is at page bottom (overscroll bounce
  // fires false scroll-up — keep header hidden to match TSD: bottom nav only, no header flash).
  const isHeaderHidden = useMemo(() => {
    const threshold =
      typeof document !== 'undefined' ? getStickyHeaderHeightPx() : STICKY_SCROLL_THRESHOLD;

    const pastChrome = scrollY > threshold;
    const overlaysClosed = !isMegaMenuOpen && !isSearchOpen;

    if (!hideOnScroll || !pastChrome || !overlaysClosed) {
      return false;
    }

    if (!isDesktop && isNearPageBottom(scrollY)) {
      return true;
    }

    return scrollDirection === 'down';
  }, [hideOnScroll, scrollDirection, scrollY, isMegaMenuOpen, isSearchOpen, isDesktop]);

  useEffect(() => {
    syncPdpHideOnScrollProgrammaticScroll(hideOnScroll);
  }, [hideOnScroll]);

  const setHideOnScroll = useCallback((enabled) => {
    setHideOnScrollState(enabled);
  }, []);

  const value = useMemo(
    () => ({
      headerState,
      headerStyle,
      originalState,
      isSticky,
      scrollY,
      forcesSolid,
      stickyDisabled,
      isHeaderHidden,
      isMegaMenuOpen,
      isSearchOpen,
      isHeaderHovered,
      setMegaMenuOpen,
      setSearchOpen,
      setHeaderHovered,
      setHideOnScroll,
    }),
    [
      headerState,
      headerStyle,
      originalState,
      isSticky,
      scrollY,
      forcesSolid,
      stickyDisabled,
      isHeaderHidden,
      isMegaMenuOpen,
      isSearchOpen,
      isHeaderHovered,
      setMegaMenuOpen,
      setSearchOpen,
      setHeaderHovered,
      setHideOnScroll
    ]
  );

  return <HeaderContext.Provider value={value}>{children}</HeaderContext.Provider>;
}

HeaderProvider.propTypes = {
  children: PropTypes.node.isRequired,
  configuredState: PropTypes.oneOf([
    HEADER_DESIGN_STATE.TRANSPARENT,
    HEADER_DESIGN_STATE.GRADIENT,
    HEADER_DESIGN_STATE.SOLID
  ]),
};

/**
 * useHeaderContext Hook
 *
 * Access header state from any component within HeaderProvider.
 *
 * @returns {Object} Header context value
 * @throws {Error} If used outside HeaderProvider
 *
 * @example
 * // Basic usage
 * const {headerState, headerStyle, isSticky} = useHeaderContext();
 *
 * @example
 * // Control mega menu state
 * const {setMegaMenuOpen} = useHeaderContext();
 * setMegaMenuOpen(true);
 *
 * @example
 * // Check if overlays are open
 * const {isMegaMenuOpen, isSearchOpen} = useHeaderContext();
 */
export function useHeaderContext() {
  const context = useContext(HeaderContext);

  if (context === null) {
    throw new Error('useHeaderContext must be used within HeaderProvider');
  }

  return context;
}

/**
 * Whether a click/key target inside `[data-flyout-frozen]` should remain interactive
 * (e.g. optional add-on checkboxes below the customize block).
 *
 * @param {EventTarget|null} target
 * @returns {boolean}
 */
export function isFlyoutFrozenInteractiveTarget(target) {
  if (!(target instanceof Element)) {
    return false;
  }

  return Boolean(target.closest([
    'button',
    'input',
    'select',
    'textarea',
    'a[href]',
    'label',
    '[role="button"]',
    '[role="checkbox"]',
    '[role="radio"]',
    '[role="switch"]',
    '[role="link"]',
    '[contenteditable="true"]',
    '[tabindex]:not([tabindex="-1"])',
  ].join(', ')));
}

/**
 * Props for PDP desktop flyout `.variations-section` — blocks child interaction and closes flyout on click.
 * Uses capture phase + global `[data-flyout-frozen] * { pointer-events: none }` styles.
 * Interactive descendants (checkboxes, links, buttons) stay usable.
 * @param {Function} onBlockedInteract
 * @returns {Object}
 */
export function buildDesktopPdpFlyoutFrozenProps(onBlockedInteract) {
  if (typeof onBlockedInteract !== 'function') {
    return {};
  }

  return {
    'data-flyout-frozen': 'true',
    'onClickCapture': (event) => {
      if (isFlyoutFrozenInteractiveTarget(event.target)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      onBlockedInteract();
    },
  };
}

export default HeaderContext;
